import Axios from 'axios';
import Constants from '../constants';
import { IJsonPatchDocument, IReportingUser, IValidationModel } from '../models';
import { IHierarchyReportingAccessConfig } from '../models/IHierarchyReportingAccessConfig';
import { IReportingProfileConfig } from '../models/IReportingProfileConfig';
import { APIHelper } from '../utilities/APIHelper';

export async function getUsers(surveyId: number): Promise<IReportingUser[]> {
	try {
		const res = await Axios.get(Constants.REPORTING_USERS_API_BASE.replace('{surveyId}', surveyId + ''), {
			headers: { Pragma: 'no-cache' },
		});
		if (res.status === 200) {
			return res.data;
		}
	} catch {
		/* generic error*/
	}
	throw new Error('An error occurred while trying to GetUsers');
}

export async function addUsers(surveyId: number, users: IReportingUser[]): Promise<IReportingUser[]> {
	try {
		const res = await Axios.post(Constants.REPORTING_USERS_API_BASE.replace('{surveyId}', surveyId + ''), users);
		if (res.status === 200) {
			return res.data;
		}
	} catch {
		/* generic error*/
	}
	throw new Error('An error occurred while trying to AddUsers');
}

export async function updateUsers(surveyId: number, users: IReportingUser[]): Promise<IReportingUser[]> {
	try {
		const res = await Axios.put(Constants.REPORTING_USERS_API_BASE.replace('{surveyId}', surveyId + ''), users);
		if (res.status === 200) {
			return res.data;
		}
	} catch {
		/* generic error*/
	}
	throw new Error('An error occurred while trying to UpdateUsers');
}

export async function deleteUsers(surveyId: number, userIds: number[]): Promise<IReportingUser[]> {
	try {
		const res = await Axios.delete(Constants.REPORTING_USERS_API_BASE.replace('{surveyId}', surveyId + ''), { data: userIds });
		if (res.status === 200) {
			return res.data;
		}
	} catch {
		/* generic error*/
	}
	throw new Error('An error occurred while trying to DeleteUsers');
}

export async function saveUsers(surveyId: number, users: IReportingUser[]): Promise<IValidationModel<IReportingUser[]>> {
	const res = await Axios.put(Constants.REPORTING_USERS_API_SAVE_USERS.replace('{surveyId}', surveyId + ''), users).catch(
		APIHelper.handleValidationError,
	);
	return res.data;
}

export async function notifyUser(surveyId: number, user: IReportingUser): Promise<void> {
	const res = await Axios.post(Constants.REPORTING_USERS_API_SEND_EMAIL.replace('{surveyId}', surveyId + ''), user);
	return res.data;
}

export async function getHierarchyReportingAccessConfigs(surveyId: number): Promise<IHierarchyReportingAccessConfig[]> {
	const url = Constants.HIERARCHY_REPORTING_ACCESS_CONFIGS_API.replace('{surveyId}', surveyId + '');
	const res = await Axios.get(url);
	return res.data;
}

export async function addHierarchyReportingAccessConfig(
	surveyId: number,
	model: IHierarchyReportingAccessConfig,
): Promise<IValidationModel<IHierarchyReportingAccessConfig>> {
	const url = Constants.HIERARCHY_REPORTING_ACCESS_CONFIGS_API.replace('{surveyId}', surveyId + '');
	const res = await Axios.post(url, model);
	return res.data;
}

export async function deleteHierarchyReportingAccessConfig(surveyId: number, model: IHierarchyReportingAccessConfig): Promise<void> {
	let url = Constants.HIERARCHY_REPORTING_ACCESS_CONFIGS_API.replace('{surveyId}', surveyId + '');
	url = url + '/' + model.hierarchyReportingAccessConfigId;
	await Axios.delete(url);
}

export async function updateHierarchyReportingAccessConfig(
	surveyId: number,
	model: IHierarchyReportingAccessConfig,
): Promise<IValidationModel<IHierarchyReportingAccessConfig>> {
	const url = Constants.HIERARCHY_REPORTING_ACCESS_CONFIGS_API.replace('{surveyId}', surveyId + '');
	const res = await Axios.put(url, model);
	return res.data;
}

export async function patchHierarchyReportingAccessConfigUserCount(
	surveyId: number,
	hierarchyReportingAccessConfigId: number,
	userCount: number,
): Promise<void> {
	const patchDocument: IJsonPatchDocument[] = [
		{
			op: 'replace',
			path: 'userCount',
			value: userCount,
		},
	];
	const url =
		Constants.HIERARCHY_REPORTING_ACCESS_CONFIGS_API.replace('{surveyId}', surveyId + '') + `/${hierarchyReportingAccessConfigId}`;
	await Axios.patch(url, patchDocument);
}

export async function getReportingProfileConfigs(surveyId: number): Promise<IReportingProfileConfig[]> {
	const url = Constants.REPORTING_PROFILE_CONFIGS_API.replace('{surveyId}', surveyId + '');
	const res = await Axios.get<IReportingProfileConfig[]>(url);
	return res.data;
}

export async function saveReportingProfileConfig(
	surveyId: number,
	config: IReportingProfileConfig,
): Promise<IValidationModel<IReportingProfileConfig>> {
	const url = Constants.REPORTING_PROFILE_CONFIGS_API.replace('{surveyId}', surveyId.toString());
	const res = await Axios.put<IValidationModel<IReportingProfileConfig>>(url, config);
	return res.data;
}

export async function patchReportingProfileConfigUserCount(
	surveyId: number,
	reportingProfileConfigId: number,
	userCount: number,
): Promise<void> {
	const patchDocument: IJsonPatchDocument[] = [
		{
			op: 'replace',
			path: 'userCount',
			value: userCount,
		},
	];
	const url = Constants.REPORTING_PROFILE_CONFIGS_API.replace('{surveyId}', surveyId + '') + `/${reportingProfileConfigId}`;
	await Axios.patch(url, patchDocument);
}
